// src/pages/ToolsPage.js
import React from 'react';
import ToolCard from '../components/tools/ToolCard';

/** Add new tools here – the grid renders automatically */
const TOOLS = [
  {
    id: 'pdf-editor',
    icon: '📄',
    name: 'PDF Editor',
    route: '/tools/pdf-editor',
    description:
      'AI-powered PDF editor that lets you edit text like Word, replace images, adjust fonts, and enhance documents instantly.',
  },
  {
    id: 'image-converter',
    icon: '🖼️',
    name: 'Image Converter',
    route: '/tools/image-converter',
    description:
      'Convert image formats instantly: JPG/JPEG ↔ PNG, WEBP, BMP, GIF, TIFF, and ICO.',
  },
  {
    id: 'json-formatter',
    icon: '🧩',
    name: 'JSON Formatter',
    route: '/tools/json-formatter',
    description:
      'Validate, format, and minify JSON for APIs and configs instantly.',
  },
  {
    id: 'text-case-converter',
    icon: '🔤',
    name: 'Text Case Converter',
    route: '/tools/text-case-converter',
    description:
      'Convert text into Title Case, snake_case, kebab-case, uppercase, and more.',
  },
  {
    id: 'password-generator',
    icon: '🔐',
    name: 'Password Generator',
    route: '/tools/password-generator',
    description:
      'Generate strong random passwords with custom length and character rules.',
  },
  {
    id: 'base64-tool',
    icon: '🧬',
    name: 'Base64 Encoder/Decoder',
    route: '/tools/base64-tool',
    description:
      'Encode plain text to Base64 and decode Base64 back to text quickly.',
  },
  {
    id: 'pdf-toolkit',
    icon: '🗂️',
    name: 'PDF Toolkit',
    route: '/tools/pdf-toolkit',
    description:
      'Merge, split, rotate, and compress PDFs in one place.',
  },
  {
    id: 'bg-upscale',
    icon: '✨',
    name: 'BG Remover + Upscaler',
    route: '/tools/bg-upscale',
    description:
      'Remove image backgrounds and upscale low-resolution images.',
  },
  {
    id: 'ocr',
    icon: '🔎',
    name: 'OCR Extractor',
    route: '/tools/ocr',
    description:
      'Extract text from images and PDFs using OCR.',
  },
  {
    id: 'resume-ats',
    icon: '📋',
    name: 'Resume ATS Checker',
    route: '/tools/resume-ats',
    description:
      'Compare resume text with a job description and get an ATS score.',
  },
  {
    id: 'invoice-generator',
    icon: '🧾',
    name: 'Invoice Generator',
    route: '/tools/invoice-generator',
    description:
      'Generate simple professional invoice PDFs instantly.',
  },
  {
    id: 'email-rewrite',
    icon: '✉️',
    name: 'AI Email Rewrite',
    route: '/tools/email-rewrite',
    description:
      'Rewrite emails in professional, friendly, or concise tones.',
  },
  {
    id: 'grammar-plagiarism',
    icon: '🧠',
    name: 'Grammar + Plagiarism',
    route: '/tools/grammar-plagiarism',
    description:
      'Quick grammar hints and plagiarism-risk heuristics for text.',
  },
  {
    id: 'url-toolkit',
    icon: '🔗',
    name: 'URL Toolkit',
    route: '/tools/url-toolkit',
    description:
      'Create short links and build UTM-tagged campaign URLs.',
  },
  {
    id: 'zip-unzip',
    icon: '🗜️',
    name: 'ZIP / UNZIP',
    route: '/tools/zip-unzip',
    description:
      'Compress multiple files and extract zip archives.',
  },
  {
    id: 'qr-barcode',
    icon: '📷',
    name: 'QR + Barcode Tools',
    route: '/tools/qr-barcode',
    description:
      'Generate and scan QR codes and generate barcodes.',
  },
  // ── future tools ─────────────────────────────────────────────────────────
  // {
  //   id: 'resume-optimizer',
  //   icon: '📝',
  //   name: 'Resume Optimizer',
  //   route: '/tools/resume-optimizer',
  //   description: 'AI-powered resume optimizer that tailors your resume to any job description in seconds.',
  // },
  // {
  //   id: 'invoice-generator',
  //   icon: '🧾',
  //   name: 'Invoice Generator',
  //   route: '/tools/invoice-generator',
  //   description: 'Generate clean, professional invoices instantly and export them as PDF.',
  // },
  // {
  //   id: 'ai-contract-cleaner',
  //   icon: '⚖️',
  //   name: 'AI Contract Cleaner',
  //   route: '/tools/ai-contract-cleaner',
  //   description: 'Paste any contract and let AI highlight risky clauses & suggest plain-English rewrites.',
  // },
];

const ToolsPage = ({ setPage }) => {
  return (
    <main style={{ flex: 1, padding: '60px 16px 100px' }}>
      <div style={{ maxWidth: '72rem', margin: '0 auto' }}>

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 52, textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            padding: '4px 14px', borderRadius: 9999, marginBottom: 16,
            background: 'rgba(0,221,255,0.06)',
            border: '1px solid rgba(0,221,255,0.2)',
            fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
            color: '#00ddff', textTransform: 'uppercase',
          }}>
            Syncusive Tools
          </div>
          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 46px)',
            fontWeight: 800, color: '#f8fafc',
            letterSpacing: '-0.03em', margin: '0 0 16px',
          }}>
            Your AI-Powered Toolkit
          </h1>
          <p style={{
            fontSize: 16, color: '#64748b', maxWidth: 520, margin: '0 auto',
            lineHeight: 1.7,
          }}>
            A collection of powerful tools built to save you time, enhance your
            documents, and supercharge your workflow.
          </p>
        </div>

        {/* ── Tool grid ────────────────────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 24,
        }}>
          {TOOLS.map((tool) => (
            <ToolCard
              key={tool.id}
              icon={tool.icon}
              name={tool.name}
              description={tool.description}
              route={tool.route}
              setPage={setPage}
            />
          ))}

          {/* Coming Soon placeholder card */}
          <div style={{
            background: 'rgba(255,255,255,0.015)',
            border: '1px dashed rgba(255,255,255,0.10)',
            borderRadius: 16, padding: '28px 24px',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 12, minHeight: 220,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'rgba(255,255,255,0.04)',
              border: '1px dashed rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22,
            }}>
              ⏳
            </div>
            <p style={{ fontSize: 14, color: '#475569', textAlign: 'center', margin: 0 }}>
              More tools coming soon...
            </p>
          </div>
        </div>

      </div>
    </main>
  );
};

export default ToolsPage;
